import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PublicHeader from "@/components/layout/PublicHeader";
import { Play, X, Clock } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function Tips() {
  const [tips, setTips] = useState([]);
  const [active, setActive] = useState(null);
  useSEO({
    title: "Career Tips — Maqam",
    description: "Short video tips to land your next job in Saudi Arabia. CV, interviews, negotiation, and career moves.",
    path: "/tips",
  });

  useEffect(() => {
    api.get("/tips").then((r) => setTips(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-white text-black">
      <PublicHeader />

      <section className="border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">/ Tips from the experts</div>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-3xl">
            How to <span className="text-gradient">win the spot.</span>
          </h1>
          <p className="text-lg text-gray-700 mt-6 max-w-2xl">
            Curated video lessons from world-class speakers on communication, mindset, negotiation and interview
            performance. Watch before your next interview.
          </p>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          {tips.length === 0 ? (
            <div className="text-gray-500 font-mono text-sm">loading…</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tips-grid">
              {tips.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActive(t)}
                  className="text-start bg-white border border-black/10 hover:shadow-[6px_6px_0_0_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all group"
                  data-testid={`tip-card-${t.id}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={`https://img.youtube.com/vi/${t.video_id}/hqdefault.jpg`}
                      alt={t.title}
                      className="w-full aspect-video object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => { e.target.src = `https://img.youtube.com/vi/${t.video_id}/mqdefault.jpg`; }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                      <div className="w-14 h-14 bg-white flex items-center justify-center">
                        <Play className="w-6 h-6 text-black fill-black ms-0.5" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 end-2 bg-black text-white px-2 py-1 text-xs font-mono flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {t.duration_min} min
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-2">
                      {t.category}
                    </div>
                    <h3 className="font-display font-bold text-lg tracking-tight mb-2 leading-tight">{t.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 leading-relaxed">{t.description}</p>
                    <div className="flex items-center gap-2 pt-3 border-t border-black/10">
                      <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-display font-black text-sm flex-shrink-0">
                        {t.expert.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-bold text-sm truncate">{t.expert}</div>
                        <div className="text-xs text-gray-500 truncate">{t.role}</div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" data-testid="tip-modal">
          <div className="bg-white w-full max-w-4xl border border-black/10 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-black/10">
              <div className="min-w-0">
                <div className="font-display font-bold text-lg truncate">{active.title}</div>
                <div className="text-xs text-gray-500">
                  {active.expert} · {active.role}
                </div>
              </div>
              <button onClick={() => setActive(null)} className="p-2 hover:bg-gray-100" data-testid="tip-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${active.video_id}?autoplay=1`}
                title={active.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-6">
              <p className="text-gray-800 leading-relaxed">{active.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
